import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog } from "../Base/Dialog";
import { Loading } from "../Base";
import {
  batchBonusApi,
  BATCH_BONUS_UPDATED_EVENT,
  BATCH_BONUS_CLAIM_PATH,
} from "../../api/batchBonusApi";
import { useNavigation, ROUTES } from "../../utils/navigation";
import { useT } from "../../i18n/hooks";
import { useCurrencySymbol } from "../../hooks/useUtils";
import utils from "../../utils";
import { useNotificationStore } from "../../modules/notification/stores";
import { useUserStore } from "../../modules/user/stores";
import "./BatchBonusClaimDialog.scss";

const formatDate = (v: string | number | null | undefined): string => {
  if (v == null || v === "") return "";
  const dt = new Date(v);
  if (isNaN(dt.getTime())) return String(v);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

interface ClaimResultState {
  status: string;
  amount: number;
  iconUrl: string;
  endTime?: string | number | null;
}

/** 批量赠金领取网关：只在领取路径触发 */
export const BatchBonusClaimGate: React.FC = () => {
  const { pathname, search } = useLocation();
  const { navigate } = useNavigation();
  const t = useT();
  const symbol = useCurrencySymbol();
  const processedRef = useRef<string | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [result, setResult] = useState<ClaimResultState | null>(null);

  useEffect(() => {
    // 离开领取页要重置幂等锁，否则返回再进会空白
    if (pathname !== BATCH_BONUS_CLAIM_PATH) {
      processedRef.current = null;
      setClaiming(false);
      setResult(null);
      return;
    }
    const id = new URLSearchParams(search).get("id");
    if (!id) {
      navigate(ROUTES.HOME, { replace: true });
      return;
    }
    if (processedRef.current === id) {
      return;
    }
    processedRef.current = id;

    void (async () => {
      setClaiming(true);
      setResult(null);
      let next: ClaimResultState = { status: "FAILED", amount: 0, iconUrl: "" };
      try {
        const resp = (await batchBonusApi.claim(id)) as any;
        const model = resp?.data?.model ?? resp?.data ?? {};
        const iconRaw =
          useNotificationStore.getState().batchBonusIcon || model.icon;
        next = {
          status: model.status || "FAILED",
          amount: Number(model.amount) || 0,
          iconUrl: iconRaw ? utils.getImageUrl(iconRaw) : "",
          endTime: model.endTime,
        };
      } catch {
        next = { status: "FAILED", amount: 0, iconUrl: "" };
      }
      if (next.status === "OK") {
        window.dispatchEvent(new Event(BATCH_BONUS_UPDATED_EVENT));
        useUserStore.getState().loadUserRealtime();
      }
      // 只认当前 URL 的 id，防旧请求覆盖新结果
      if (new URLSearchParams(window.location.search).get("id") === id) {
        setClaiming(false);
        setResult(next);
      }
    })();
    // 不在 cleanup 里取消：effect 重跑会误伤正常领取
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search]);

  const handleClose = () => {
    // 重置幂等锁允许重试，后端幂等不会重复发放
    processedRef.current = null;
    setResult(null);
    navigate(ROUTES.HOME, { replace: true });
  };

  if (pathname !== BATCH_BONUS_CLAIM_PATH) {
    return null;
  }
  if (claiming) {
    return (
      <Dialog
        visible
        size="sm"
        className="batch-bonus-claim-dialog"
        getContainer={() => document.body}
      >
        <div className="batch-bonus-claim-dialog__body">
          <Loading variant="app" />
        </div>
      </Dialog>
    );
  }

  if (!result) {
    return null;
  }

  const isSuccess = result.status === "OK";
  let message: string;
  if (result.status === "OK") {
    message = t("batchBonus.successMessage");
  } else if (result.status === "EXPIRED") {
    message = t("batchBonus.expiredMessage", { date: formatDate(result.endTime) });
  } else if (result.status === "ALREADY_CLAIMED") {
    message = t("batchBonus.alreadyClaimedMessage");
  } else {
    message = t("batchBonus.failed");
  }

  return (
    <Dialog
      visible
      size="sm"
      className="batch-bonus-claim-dialog"
      confirmText={t("common.confirm")}
      onConfirm={handleClose}
      onClose={handleClose}
      getContainer={() => document.body}
    >
      <div className="batch-bonus-claim-dialog__body">
        {isSuccess && result.iconUrl ? (
          <div className="batch-bonus-claim-dialog__icon-wrap">
            <img
              className="batch-bonus-claim-dialog__icon"
              src={result.iconUrl}
              alt=""
            />
          </div>
        ) : null}
        {isSuccess ? (
          <h3 className="batch-bonus-claim-dialog__title">
            {t("batchBonus.successTitle")}
          </h3>
        ) : null}
        <div className="batch-bonus-claim-dialog__content">{message}</div>
        {isSuccess && result.amount > 0 ? (
          <div className="batch-bonus-claim-dialog__amount">
            +{symbol}
            {result.amount.toFixed(2)}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
};

export default BatchBonusClaimGate;
